/**
 * The daily challenge card on the front page, and the day's pick behind it.
 *
 * One fight a day, the same for everyone — plan/daily-challenge.md. What the
 * card shows is what a player needs to size the fight up before committing to
 * it: what it is called, a line about it, the board with both forces on it,
 * what each side is worth, and how many people have taken it on.
 *
 * Everything except the attempt count comes from content/daily.json, which is
 * bundled with the page. The front page is served from S3 and holds its own
 * copy of everything on it, so the card is up whenever the bucket is; the
 * count is the one live figure, and it is left out rather than allowed to keep
 * the card waiting.
 *
 * daily.json is generated, not written: `mms index` builds it from the
 * scenario files and arena's report of what they are worth. The blurb is the
 * scenario's own `Description=` line, so the card and the file a player
 * downloads say the same thing.
 *
 * The map is drawn the way the lobby's force cards and the scenario preview
 * draw theirs — MegaMek's own board tile and unit art, recoloured pixel by
 * pixel through preview.ts — so what it shows is the art the match will use
 * rather than an impression of it.
 */

import {
  currentSession,
  dailyStatus,
  type DailyStatus,
  type Scenario,
} from "../api";
import { stashDailyPlay } from "../daily-play-stash";
import { el, render } from "../dom";
import { nav } from "../nav";
import { SPRITE_H, SPRITE_W } from "../camo/mech";
import { ghost } from "../megamek-art";
import dailyJson from "../content/daily.json";
// From designation.ts and not preview.ts: that module names every unit
// sprite the site has, and importing it here would put the whole map in the
// front page's eager bundle - which analytics.test.mjs fails the build over.
import { chassisOf, modelOf } from "../scenario/designation";
import { eraFor, eraSpan } from "../content/eras";
import {
  overridesFor,
  type Override,
  type RawOverrides,
} from "../content/overrides";
import { ERA_ICONS } from "../content/trivia";
import { drawMinimap, haveBoards, minimapSize } from "./minimap";
import { isPhone } from "../phone";
import { phoneGateScreen } from "./phone-gate";
import { sideCss, sideRgb, type RGB } from "../content/sides";

/**
 * A unit on the schematic board: which machine, which row it stands in, and
 * which of MegaMek's six facings the scenario gave it.
 *
 * There is no column. Where a unit stands along its row is `drawMap`'s to
 * decide, because the card spreads a row evenly and only the order the units
 * arrive in survives that.
 */
export type Placement = {
  name: string;
  row: number;
  facing: string;
  /**
   * What this machine is worth with the crew this scenario puts in it.
   *
   * Per scenario and not per design, because BV 2.0 multiplies by gunnery and
   * piloting: the same Stinger STG-3R is 359 in one daily and 323 in another.
   * That is also why it is here rather than in content/units.ts, which holds
   * only what is true of a design wherever it turns up.
   *
   * Absent when the report a build was made from had nothing to say, so every
   * reader has to cope with not knowing.
   */
  bv?: number;
  /**
   * The crew, when the scenario names or rates one.
   *
   * The skills are what BV 2.0 multiplies by, so they are what explains the
   * figure beside them. The name is the file's own; a V2 crew that gives only
   * skills has none.
   */
  pilot?: string;
  gunnery?: number;
  piloting?: number;
};

/** One force: what it fields and what it is worth. */
export type Side = {
  name: string;
  /**
   * The MegaMek team this faction fights on, when the scenario sets one.
   *
   * A *side* in the scoring sense is a team, not a faction: Bug Company puts
   * three pirate lances on one team so each can carry its own camo and its own
   * deployment edge. Absent on the two-faction files, where the first faction
   * is the player's and the second is the opposition.
   */
  team?: number;
  /**
   * The deployment zone the scenario gave this force — one of MegaMek's own
   * names: `N`, `SW`, `CTR` and the rest. Absent when the file says `Any`.
   *
   * The card stands each force in the part of the board it deploys on, so a
   * fight with three lances closing from three directions looks like one.
   */
  start?: string;
  /** Total battle value, as MegaMek computes it for these pilots. BV 2.0. */
  bv: number;
  /**
   * Total mass and price of the force, summed over its machines.
   *
   * Optional because they come from MegaMek's unit cache rather than the
   * scenario file, so an index built before arena reported them has neither.
   */
  tons?: number;
  cost?: number;
  units: Placement[];
};

/**
 * One day's fight.
 *
 * `date` is the UTC day this is the daily challenge, and it is authored: the
 * pick is a schedule rather than a computation over the list, so appending an
 * eleventh challenge cannot change which fight the sixth of the month was.
 */
export type Challenge = {
  slug: string;
  /**
   * What this fight changes about the game: rules it switches, and the
   * behaviour it gives the bots. Written by `mms index` from the scenario's V2
   * form, and absent for a challenge that changes nothing.
   */
  overrides?: RawOverrides;
  /**
   * The board files the fight is on, and how many across. Written by
   * `mms index` from the scenario's `map:`; the shapes themselves are in
   * content/boards.json, which several challenges share.
   */
  map?: { cols: number; boards: string[] };
  date: string;
  name: string;
  blurb: string;
  /** Introduction years of the oldest and newest design in the fight. */
  years: { from: number; to: number };
  board: { columns: number; rows: number };
  sides: Side[];
};

// The annotation is the check: a file that does not fit fails tsc rather than
// the page.
const challenges: Challenge[] = dailyJson.challenges;

/**
 * Today in UTC, as `YYYY-MM-DD`.
 *
 * UTC because "the same fight as everyone else" has to mean the same fight:
 * a per-player day would roll over at a different moment for every timezone
 * and make the claim untrue. It costs some players a rollover mid-evening.
 */
export function utcDay(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/**
 * The challenge for a given UTC day: the one scheduled for it, or the most
 * recent one before it.
 *
 * Falling back to the latest past challenge rather than to nothing is what
 * keeps the front page from going blank the day the schedule runs out. It is
 * a stale card rather than an empty one, and `isCurrent` below is what says
 * which it is.
 */
export function challengeFor(day: string): Challenge | null {
  const past = challenges.filter((c) => c.date <= day);
  if (past.length === 0) return null;
  return past.reduce((latest, c) => (c.date > latest.date ? c : latest));
}

/** Every challenge whose day has come, most recent first. */
export function archive(day: string): Challenge[] {
  return challenges
    .filter((c) => c.date <= day)
    .sort((a, b) => (a.date < b.date ? 1 : -1));
}

/** One challenge by its slug, or null when the site does not have it. */
export function challengeBySlug(slug: string): Challenge | null {
  return challenges.find((c) => c.slug === slug) ?? null;
}

/**
 * The day's fight in the shape the lobby screen draws forces from.
 *
 * A daily scenario is not in `/api/scenarios` at all — the files are compiled
 * into the API and carried in the launch manifest — so the lobby's daily mode
 * builds its one Scenario from the same bundled file the card draws rather
 * than asking a catalog that has never heard of it.
 *
 * The sides come out in the file's own order, which is the order the API's
 * `PLAYER_SLOT` and `BOT_SLOT` are in: the player's force first, the bot's
 * second. daily.test.mjs is what says the two still agree.
 */
export function dailyScenario(challenge: Challenge): Scenario {
  return {
    path: scenarioHref(challenge.slug),
    name: challenge.name,
    description: challenge.blurb,
    slots: challenge.sides.map((side) => ({
      name: side.name,
      team: side.team ?? null,
      start: side.start,
      units: side.units.map((unit) => ({
        name: unit.name,
        pilot: unit.pilot,
        gunnery: unit.gunnery,
        piloting: unit.piloting,
      })),
    })),
  };
}

/** Where a challenge's scenario file is served from. */
export function scenarioHref(slug: string): string {
  return `/daily/${slug}.mms`;
}

/**
 * Whether a facing points down the card rather than up it.
 *
 * MegaMek has six facings and the sprites are all drawn facing north, so the
 * card quantises to the two turns it can make without resampling: the three
 * southerly facings turn the art around, the three northerly ones leave it.
 * A 60-degree turn of pixel art this size looks worse than no turn at all.
 */
function facesSouth(facing: string): boolean {
  return facing === "S" || facing === "SE" || facing === "SW";
}

/** Where one machine stands on the schematic board, in canvas pixels. */
export type Spot = {
  unit: Placement;
  x: number;
  y: number;
  /** Width of the slot this unit owns, for sizing the tag that labels it. */
  slot: number;
};

/**
 * How many tile rows of ground the board is drawn on, for a scenario that
 * deploys its forces `rows` apart.
 *
 * Twice the scenario's own, so the two forces stand at the outer edges of a
 * field with real ground between them rather than nose to nose. The extra
 * space is where the unit tags and the era watermark live.
 */
export function renderRows(rows: number): number {
  return rows * 2;
}

/**
 * Bare field above and below the outermost rank, in pixels.
 *
 * A force deploys on its own edge, and its machines carry their names under
 * them, so without this the near force's tags hang off the bottom of the
 * picture and are clipped. About one tag deep, which is all it takes.
 */
const FIELD_MARGIN = 40;

/**
 * Bare field above the topmost rank.
 *
 * Much less than below it, and not for symmetry's sake: every machine carries
 * its name *under* it, so the bottom rank needs a tag's depth of clear ground
 * and the top rank needs none. Equal margins left the top force looking like
 * it had drifted down the board.
 */
const TOP_MARGIN = 8;

/** The height of the drawn field, margins included. */
export function fieldHeight(rows: number): number {
  return renderRows(rows) * SPRITE_H + FIELD_MARGIN * 2;
}

/**
 * How much clear ground to leave at either end of a rank, as a fraction of
 * the board's width.
 *
 * A rank spread edge to edge puts its outermost machines half off the
 * picture and leaves their tags nowhere to go. An eighth of the board at each
 * end is enough that the outermost tag has its own room.
 */
const SIDE_MARGIN = 0.08;

/**
 * The hex field, drawn rather than tiled.
 *
 * There is a hex texture in the assets and it cannot be used here: MegaMek's
 * hexes are 84 wide by 72 tall and interlock, so adjacent columns step 63
 * across and every other one drops half a hex. The repeating unit of that
 * lattice is 126 wide, and the texture is 84 — tiling it lays every third
 * column of hexes on top of the previous one's edge, which is the broken band
 * that showed up at each seam. It also carries a single bright pixel at its
 * centre, which became a regular grid of dots.
 *
 * Drawing it costs one path and gets a field that is correct at any size,
 * which the taller board needs. The two colours are the texture's own, so the
 * board still looks like the rest of the site.
 */
const HEX_GROUND = "rgb(11 16 23)";
const HEX_LINE = "rgb(23 33 52)";

function drawHexField(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): void {
  ctx.fillStyle = HEX_GROUND;
  ctx.fillRect(0, 0, width, height);

  // Flat top and bottom, points left and right: 42 from centre to either
  // point, 36 from centre to either flat. sin(60°) turns the unit hexagon's
  // vertical reach into exactly 36, so the hex is 72 tall and not 72.7.
  const reach = SPRITE_W / 2;
  const flat = SPRITE_H / 2;
  const pitch = reach * 1.5;
  const sin60 = Math.sin(Math.PI / 3);

  ctx.strokeStyle = HEX_LINE;
  ctx.lineWidth = 1;
  ctx.beginPath();
  // From -1 so the row and column that only half show still draw their edges;
  // without it the field starts with a band of cut-off hexes.
  for (let col = -1; col * pitch <= width + reach; col++) {
    const cx = col * pitch;
    const drop = col % 2 === 0 ? 0 : flat;
    for (let row = -1; row * SPRITE_H <= height + SPRITE_H; row++) {
      const cy = row * SPRITE_H + drop;
      for (let vertex = 0; vertex <= 6; vertex++) {
        const angle = (Math.PI / 3) * vertex;
        const x = cx + reach * Math.cos(angle);
        const y = cy + (flat * Math.sin(angle)) / sin60;
        if (vertex === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
    }
  }
  ctx.stroke();
}

/**
 * Where each of a force's machines stands.
 *
 * A row at a time, because the spread is per row: unit i of the n in one is
 * centred at (i + 0.5) / n of the width, which comes out even for every n.
 * Whole tiles cannot — five units do not divide eight tiles — and the
 * half-spaced middle pair that produced is what this replaces.
 *
 * Alternate units then step half a rank towards the middle of the board, so a
 * force reads as a formation rather than a firing line. The step is always
 * *towards* the empty ground between the two sides: a force deployed on the
 * top edge steps down and one on the bottom edge steps up, so neither walks
 * off the picture.
 *
 * Shared with the tags in `boardFigure` rather than duplicated there, which is
 * what stops a label from drifting off the machine it names.
 */
/**
 * The part of the board a force stands in, as fractions of the field.
 *
 * MegaMek's deployment zones are the fiction here: a scenario that sends three
 * lances in from the north, south-west and south-east should draw as three
 * forces converging, not as three ranks stacked on top of each other. Each
 * zone claims a horizontal band and a vertical one; a force is then laid out
 * inside its own box by the same rank logic that lays out a single force.
 *
 * `CTR` is deliberately narrow. It holds one machine in every scenario that
 * uses it so far, and a wide centre box would put that machine's tag under a
 * lance closing from the north.
 */
/**
 * Where a force stands, derived rather than declared.
 *
 * The rule the layout grid forced out: **a force's depth depends on how many
 * machines it has**, so a fixed box is always wrong for some count. One
 * machine in a box sized for six floats at the back of it; six in a box sized
 * for two hang out of the bottom. Both were happening.
 *
 * So a zone gives three things — the horizontal band it owns, the field edge
 * it entered by, and how deep its front rank stands — and the block's height
 * comes from the force. Ranks then stack *away* from the enemy, which puts a
 * lone machine at the front where it belongs.
 */
export type Zone = {
  /** Horizontal band, as fractions of the board. */
  x0: number;
  x1: number;
  /** Which way the force faces, and so which way its ranks stack. */
  facing: "down" | "up" | "middle";
  /** How far the front rank stands from its own edge, as a fraction. */
  depth: number;
};

export function zoneFor(start: string | undefined): Zone {
  const zone = (start ?? "").toUpperCase().replace(" (DEEP)", "");
  // Horizontal: a compass zone with W in it owns the left half, one with E the
  // right half, everything else the full width.
  const x0 = zone.includes("W") ? 0 : zone.includes("E") ? 0.5 : 0;
  const x1 = zone.includes("W") ? 0.5 : zone.includes("E") ? 1 : 1;

  if (zone.startsWith("N")) return { x0, x1, facing: "down", depth: 0.34 };
  if (zone.startsWith("S")) return { x0, x1, facing: "up", depth: 0.34 };
  // CTR and the bare compass points E and W hold the middle. `Any` lands here
  // too, which is right: a force with no zone is not on an edge.
  return { x0, x1, facing: "middle", depth: 0 };
}

/**
 * The block of field a force actually occupies, in pixels.
 *
 * Exported for the layout probe, which draws it: a placement bug is far easier
 * to see as a box in the wrong place than to infer from where sprites landed.
 */
export function forceBlock(
  challenge: Challenge,
  side: Side,
  count: number,
): { left: number; right: number; top: number; bottom: number; rows: number } {
  const width = challenge.board.columns * SPRITE_W;
  const height = fieldHeight(challenge.board.rows);
  const zone = zoneFor(side.start);

  const left = width * zone.x0;
  const right = width * zone.x1;
  const shape = formation(count);
  const rows = count === 0 ? 0 : Math.max(...shape.map((p) => p.row)) + 1;
  const deep = rows * SPRITE_H;

  const usable = height - TOP_MARGIN - FIELD_MARGIN;
  let top: number;
  if (zone.facing === "down") {
    // Entered from the north: the front rank stands `depth` into the field and
    // the rest of the force is behind it, towards the top edge.
    top = TOP_MARGIN + usable * zone.depth - deep;
  } else if (zone.facing === "up") {
    top = TOP_MARGIN + usable * (1 - zone.depth);
  } else {
    // The middle: centred, then nudged below the era mark, which sits at 41%
    // of the field whenever anything holds the centre.
    top = TOP_MARGIN + usable * 0.52 - deep / 2;
  }
  // Never off the field, whatever the count.
  top = Math.max(TOP_MARGIN, Math.min(top, TOP_MARGIN + usable - deep));
  return { left, right, top, bottom: top + deep, rows };
}

export function spotsFor(challenge: Challenge, side: Side): Spot[] {
  const spots: Spot[] = [];
  for (const row of new Set(side.units.map((unit) => unit.row))) {
    const line = side.units.filter((unit) => unit.row === row);
    const block = forceBlock(challenge, side, line.length);
    const facing = zoneFor(side.start).facing;

    // Inset so the outermost machine is on the picture and its tag has room.
    const span = block.right - block.left;
    const inset = Math.round(Math.min(span * SIDE_MARGIN, SPRITE_W * 0.6));
    const usable = Math.max(SPRITE_W, span - inset * 2);
    const left = block.left + inset;

    const shape = formation(line.length);
    for (const [i, unit] of line.entries()) {
      const place = shape[i]!;
      const inRow = shape.filter((p) => p.row === place.row).length;
      // Rank 0 is the front. A force entering from the north has its front
      // rank lowest on the board, so higher ranks step *up*; one from the
      // south steps down. Either way a lone machine stands at the front.
      const step =
        facing === "down"
          ? block.bottom - SPRITE_H - place.row * SPRITE_H
          : block.top + place.row * SPRITE_H;
      spots.push({
        unit,
        x: Math.round(left + place.at * usable - SPRITE_W / 2),
        y: Math.round(step),
        // A tag never wider than the row it stands in, so two cannot meet.
        slot: usable / Math.max(1, inRow),
      });
    }
  }
  return spots;
}

/** One machine's place in a formation: which row, and where along it. */
type Place = { row: number; at: number };

/**
 * Where the machines of one force stand, as fractions of its box.
 *
 * A lance is almost never more than six machines, and each of those counts has
 * a shape a player recognises, so they are written out rather than derived. A
 * general rule produced a lone machine hard against the left edge of its box
 * and a pair sitting a third of the way in, both of which read as a mistake.
 *
 * Row 0 is nearest the edge the force deployed on; higher rows sit further
 * into the board.
 *
 *     1: one, centred          2: two, evenly spaced
 *     3: a triangle, point in  4: two by two, staggered
 *     5: a star, one centred   6: three by three, staggered
 *
 * Above six it falls back to rows of three, which keeps the same rhythm.
 */
export function formation(count: number): Place[] {
  switch (count) {
    case 0:
      return [];
    case 1:
      return [{ row: 0, at: 0.5 }];
    case 2:
      return [
        { row: 0, at: 1 / 3 },
        { row: 0, at: 2 / 3 },
      ];
    case 3:
      return [
        { row: 0, at: 0.25 },
        { row: 0, at: 0.75 },
        { row: 1, at: 0.5 },
      ];
    case 4:
      // Two by two, and the rows lean opposite ways so the block as a whole
      // still sits centred while the machines interlock.
      return [
        { row: 0, at: 0.25 },
        { row: 0, at: 0.65 },
        { row: 1, at: 0.35 },
        { row: 1, at: 0.75 },
      ];
    case 5:
      return [
        { row: 0, at: 0.25 },
        { row: 0, at: 0.75 },
        { row: 1, at: 0.5 },
        { row: 2, at: 0.25 },
        { row: 2, at: 0.75 },
      ];
    case 6:
      return [
        { row: 0, at: 0.15 },
        { row: 0, at: 0.45 },
        { row: 0, at: 0.75 },
        { row: 1, at: 0.25 },
        { row: 1, at: 0.55 },
        { row: 1, at: 0.85 },
      ];
    default: {
      // Rows of three, leaning alternately, for the rare force bigger than a
      // lance. Same rhythm as the six.
      const places: Place[] = [];
      for (let i = 0; i < count; i++) {
        const row = Math.floor(i / 3);
        const lean = row % 2 === 0 ? -0.05 : 0.05;
        places.push({ row, at: ((i % 3) + 0.5) / 3 + lean });
      }
      return places;
    }
  }
}

/**
 * The board: the hex field laid across it, then each side's units stood on it
 * in that side's colour, evenly spread and facing the way the scenario has
 * them facing.
 *
 * Drawn after the card is already on the page, so nothing on the front page
 * waits for it. A failure leaves the empty board behind — the figures beside
 * it are the point, and a missing picture is not worth a broken card.
 *
 * The painter is imported dynamically, and that is not an optimisation to be
 * tidied away later: preview.ts names every unit sprite the site has, and
 * pulling it into the front page's eager bundle puts a hundred kilobytes in
 * front of the hero for the sake of a picture drawn afterwards.
 * analytics.test.mjs is what holds that line.
 */
function drawMap(canvas: HTMLCanvasElement, challenge: Challenge): void {
  void (async () => {
    const { paintUnitSprite } = await import("../scenario/preview");
    const ctx = canvas.getContext("2d")!;

    // The field first, and the whole field. Nothing may be pasted back onto it
    // off the lattice — the hex lines would not meet — which is why the units
    // below are drawn as bare sprites rather than as preview.ts's
    // unit-on-a-tile.
    drawHexField(ctx, canvas.width, canvas.height);

    // putImageData replaces pixels instead of compositing them, and ignores
    // the transform — it can do neither the alpha nor the turn. So a sprite
    // is staged here and reaches the board through drawImage.
    const staging = el("canvas");
    staging.width = SPRITE_W;
    staging.height = SPRITE_H;
    const stage = staging.getContext("2d")!;

    // Every machine at once, not one after another. These are separate
    // fetches on a first visit, and awaiting them in the loop made six units
    // six round trips end to end - the board filled in one machine at a time,
    // which reads as breakage rather than as loading.
    const placed = challenge.sides.flatMap((side, index) => {
      const camo = flatCamo(sideColour(challenge, index));
      return spotsFor(challenge, side).map((spot) => ({
        ...spot,
        sprite: paintUnitSprite(spot.unit.name, camo),
      }));
    });

    // The mark each of them will stand on, drawn before any of them arrive so
    // the board is the shape it will end up being from the first frame.
    for (const { x, y } of placed) {
      stage.clearRect(0, 0, SPRITE_W, SPRITE_H);
      stage.putImageData(ghost(), 0, 0);
      ctx.drawImage(staging, x, y);
    }

    for (const { unit, x, y, sprite: pending } of placed) {
      const sprite = await pending;
      if (!sprite) continue;
      stage.clearRect(0, 0, SPRITE_W, SPRITE_H);
      stage.putImageData(sprite, 0, 0);

      // A sprite composites, so the mark under it would show through every
      // transparent pixel. The field is repainted over that one tile first,
      // clipped to it: the lattice is a function of the whole canvas, and
      // redrawing a patch of it any other way would not meet at the edges.
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, SPRITE_W, SPRITE_H);
      ctx.clip();
      drawHexField(ctx, canvas.width, canvas.height);
      ctx.restore();

      if (facesSouth(unit.facing)) {
        // Half a turn, written as scale(-1, -1) rather than rotate(PI):
        // Math.sin(Math.PI) is 1.2e-16 and not 0, and that much shear is
        // enough to make the canvas resample a sprite that should only
        // be reversed.
        ctx.setTransform(-1, 0, 0, -1, x + SPRITE_W, y + SPRITE_H);
        ctx.drawImage(staging, 0, 0);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
      } else {
        ctx.drawImage(staging, x, y);
      }
    }
  })().catch((error: unknown) => {
    console.warn("daily: the map could not be drawn", error);
  });
}

/**
 * Flat colours so the two forces read apart at map size. Not the player's own
 * camo: the card is drawn for everybody, signed in or not, and the same fight
 * has to look the same to all of them.
 *
 * Blue is the player and red is the opposition, in that order, because every
 * daily scenario names its sides in that order — so index 0 is always the
 * side the player takes. The lobby's daily mode paints the same two forces
 * from this same list rather than keeping a second copy of it.
 *
 * The red is the site's own `--danger` (#b3261e) opened up a little to sit at
 * the blue's weight on a dark map tile. It is a red rather than the burnt
 * orange it replaces: orange and the blue both read as "a colour", where red
 * and blue read as two sides.
 */
/**
 * Which team a side is on and which force of that team it is, as ranks.
 *
 * The player's team is always rank 0, so blue is always yours. Every other
 * team ranks in the order it first appears, and a force ranks within its own
 * team by the same order - so Bug Company's three pirate lances come out as
 * team 1, forces 0, 1 and 2, which `sideRgb` turns into three shades of one
 * red rather than three unrelated colours.
 */
export function sideRanks(
  challenge: Challenge,
  index: number,
): { team: number; slot: number } {
  const sides = challenge.sides;
  const side = sides[index];
  if (!side) return { team: 0, slot: 0 };
  const mine = sides[0]?.team;
  // A file that names no teams is a free-for-all, and then a faction's own
  // position is the only thing that can stand in for one.
  const teamOf = (s: Side, i: number): string =>
    s.team === undefined ? `slot:${i}` : `team:${s.team}`;
  const key = teamOf(side, index);
  const mineKey = mine === undefined ? "slot:0" : `team:${mine}`;

  const seen: string[] = [];
  for (const [i, s] of sides.entries()) {
    const k = teamOf(s, i);
    if (!seen.includes(k)) seen.push(k);
  }
  const others = seen.filter((k) => k !== mineKey);
  const team = key === mineKey ? 0 : 1 + others.indexOf(key);

  const slot = sides
    .slice(0, index)
    .filter((s, i) => teamOf(s, i) === key).length;
  return { team, slot };
}

/** A side's colour, as the pixels a flat camo is made of. */
export function sideColour(challenge: Challenge, index: number): RGB {
  const { team, slot } = sideRanks(challenge, index);
  return sideRgb(team, slot);
}

/** The same colour as CSS, for text and rules beside the board. */
export function sideCssFor(challenge: Challenge, index: number): string {
  const { team, slot } = sideRanks(challenge, index);
  return sideCss(team, slot);
}

/** A whole sprite in one colour, for a force with no camo of its own. */
export function flatCamo([r, g, b]: readonly [
  number,
  number,
  number,
]): ImageData {
  const camo = new ImageData(SPRITE_W, SPRITE_H);
  for (let i = 0; i < camo.data.length; i += 4) {
    camo.data[i] = r;
    camo.data[i + 1] = g;
    camo.data[i + 2] = b;
    camo.data[i + 3] = 255;
  }
  return camo;
}

const number = new Intl.NumberFormat();

/**
 * The board picture, a tag beside every machine on it, and the era behind
 * them.
 *
 * The tags are HTML over the canvas rather than text drawn into it: real text
 * stays crisp at any zoom, picks up the theme's colour, and can be read by
 * something that is not an eye. They are placed from the same `spotsFor` the
 * sprites are, so the two cannot drift apart.
 *
 * Chassis and model on separate lines — ATLAS over AS7-D — because the model
 * is what a reader who knows the game is actually looking for, and stacking it
 * costs no width. `modelOf` drops the parenthesised variants MegaMek's own
 * names carry: "Hellhound (Conjurer) (Standard)" tags as HELLHOUND alone,
 * since "(Conjurer) (Standard)" identifies nothing and is wider than the
 * machine it labels.
 */
export function boardFigure(challenge: Challenge): HTMLElement {
  const canvas = el("canvas", { className: "daily-map" });
  canvas.width = challenge.board.columns * SPRITE_W;
  canvas.height = fieldHeight(challenge.board.rows);
  // What the picture shows, for anyone who cannot see it. The forces are
  // named and counted rather than described hex by hex: the map is a summary,
  // and so is this.
  canvas.role = "img";
  canvas.ariaLabel = challenge.sides
    .map((side) => `${side.name}, ${side.units.length} units`)
    .join("; ");
  drawMap(canvas, challenge);

  const tags: HTMLElement[] = [];
  for (const [index, side] of challenge.sides.entries()) {
    for (const { unit, x, y, slot } of spotsFor(challenge, side)) {
      const chassis = chassisOf(unit.name);
      const model = modelOf(unit.name, chassis);
      const tag = el("span", { className: "daily-unit-tag" }, [
        el("span", { className: "daily-unit-chassis", textContent: chassis }),
        ...(model
          ? [el("span", { className: "daily-unit-model", textContent: model })]
          : []),
      ]);

      // Directly under the machine it names, and centred on it, because the
      // machine is centred in its own slot. There is room for that now the
      // field is twice as tall; beside the sprite there never was, and a tag
      // that reached sideways for room reached into its neighbour's.
      const pct = (px: number, of: number): string => `${(px / of) * 100}%`;
      tag.style.left = pct(x + SPRITE_W / 2, canvas.width);
      tag.style.top = pct(y + SPRITE_H * 0.94, canvas.height);

      // The bound that makes overlap impossible: a tag is never wider than the
      // slot its machine owns, and it is centred in that slot, so two tags
      // cannot reach each other however long their names are. The rem cap is
      // what makes a long name wrap rather than run the width of the board -
      // "Drillson Heavy Hover Tank" gets three lines, not one.
      tag.style.width = `min(${pct(slot, canvas.width)}, 7.5rem)`;
      // The colour slot, not the faction's position: three opposing lances
      // each take their own opposition colour, and the tag has to agree with
      // the sprite above it.
      // 0 is the player's, and everything else is opposition - that is all the
      // stylesheet needs to know. The exact shade rides alongside it so a tag
      // matches the sprite above it even when three lances share one team.
      tag.dataset.side = String(sideRanks(challenge, index).team);
      tag.style.setProperty("--side-colour", sideCssFor(challenge, index));
      tags.push(tag);
    }
  }

  // The era, over the empty ground between the two forces. That band is empty
  // by construction — every daily deploys its sides on opposite edges — so a
  // watermark there costs no unit any legibility, and it puts the fight's
  // period on the picture instead of in a tile competing with the numbers.
  // Dead centre, unless something deploys in the centre - which is the only
  // arrangement that would put a force there at all. An earlier version of
  // this hunted for the widest band no force occupied, and it was worse: a
  // plain north-versus-south fight whose southern force happened to be one
  // rank deeper had its era mark shoved to the top of the board for no reason
  // a reader could see. The general rule optimised something nobody asked for.
  const centreHeld = challenge.sides.some((side) =>
    (side.start ?? "").toUpperCase().startsWith("CTR"),
  );

  const era = eraFor(challenge.years.to);
  const watermark = el("span", { className: "daily-era-watermark" });
  if (centreHeld) watermark.style.top = "41%";
  if (era) {
    const url = ERA_ICONS[era.name];
    // Decorative beside the name it repeats: a mask rather than an <img>, so
    // the symbol takes the text colour on both themes. Same trick as the
    // waiting screen's trivia badge, and the same CSS rule.
    const icon = el("span", {
      className: "daily-era-icon",
      "aria-hidden": "true",
    });
    icon.hidden = !url;
    if (url) icon.style.setProperty("--era-icon", `url("${url}")`);
    // The era's own years, not the fight's: the years line in the text column
    // already says when these designs are from, and this says what period the
    // name covers - so "Star League" is dated without anyone having to know
    // the chronology.
    const span = eraSpan(era);
    watermark.append(
      icon,
      el("span", { textContent: era.name }),
      ...(span
        ? [
            el("span", {
              className: "daily-era-span",
              textContent: `(${span})`,
            }),
          ]
        : []),
    );
  }
  watermark.hidden = !era;

  return el("div", { className: "daily-map-frame" }, [
    canvas,
    watermark,
    ...tags,
  ]);
}

/** The Play button and the line beside it, plus a way to tell it what the
 * control plane said about this reader's attempt. */
export type PlayControl = {
  /** The button itself, so a caller can seat it in a split control. */
  button: HTMLButtonElement;
  /** Why it is disabled, or what the attempt scored. Empty until it is not. */
  note: HTMLElement;
  applyStatus(status: DailyStatus): void;
};

/**
 * Play: the way into the fight, on the card and on every archive row.
 *
 * One control rather than two, because the card and the archive start the same
 * fight the same way. What differs is only what each knows: the card already
 * asks for `dailyStatus` and hands it here, and the archive deliberately asks
 * the control plane nothing, so a spent attempt there comes back as the API's
 * own 409 message instead.
 *
 * Signed out, the first press is a sign-in. The slug is stashed
 * (daily-play-stash.ts) so the round trip to the provider comes back into the
 * fight rather than onto the front page — the mechanism the camo editor and a
 * shared lobby link already use, pointed at a slug.
 *
 * Signed in, this renders the lobby screen's daily mode directly rather than
 * navigating: that screen has no address of its own, the same call matches.ts
 * makes for its Duel and Scenario cards, and nav.markShown is what keeps the
 * router's idea of what is on screen from going stale behind it. The screen is
 * imported on the press, not at the top of this file, so the front page does
 * not carry the whole lobby in front of the hero.
 */
export function playControl(challenge: Challenge, day: string): PlayControl {
  const scored = challenge.date === day;
  const play = el("button", {
    type: "button",
    className: "small-btn daily-play plate-tl",
    textContent: "Play",
  });
  const note = el("span", {
    className: "hint daily-play-note",
    textContent: scored ? "" : "Does not score.",
  });

  play.addEventListener("click", () => {
    play.disabled = true;
    // The daily card is on the front page and on the archive, both of which a
    // phone can read — so this button is a way into a match from a screen the
    // gate does not stand in front of, and has to ask for itself. Before the
    // session read: whether a phone can play does not depend on who is
    // pressing. See screens/phone-gate.ts.
    if (isPhone()) {
      // The gate is not a destination, and the address has not moved: without
      // this the router still believes the front page is up, and the Home
      // link in the masthead would be a no-op against it.
      nav.markShown!(null);
      render(...phoneGateScreen());
      return;
    }
    void currentSession()
      .then(async (session) => {
        if (!session) {
          play.disabled = false;
          const { openSignIn } = await import("../signin-modal");
          openSignIn({ onBeforeConnect: () => stashDailyPlay(challenge.slug) });
          return;
        }
        const { lobbyScreen } = await import("./lobby");
        nav.markShown!("lobby");
        render(
          ...lobbyScreen(session, { kind: "daily", slug: challenge.slug }),
        );
      })
      .catch((error: unknown) => {
        console.warn("daily: the challenge could not be opened", error);
        play.disabled = false;
        note.textContent = "The control plane could not be reached.";
      });
  });

  return {
    button: play,
    note,
    applyStatus(status: DailyStatus) {
      // Only the day's own attempt is spent. A challenge whose day has passed
      // can be played again as often as anyone likes, and its note already
      // says what that is worth.
      if (!scored || !status.attempted) return;
      play.disabled = true;
      note.textContent =
        status.score === null
          ? "Already played."
          : `You scored ${number.format(status.score)}.`;
    },
  };
}

/**
 * The two forces, head to head.
 *
 * The pairing is the whole proposition of a daily challenge — the fights are
 * matched on battle value, and this is where a reader sees that before they
 * commit. Two numerals in the colours their forces fight in on the board
 * beside it, so the pairing needs no legend, and a word for how close it is,
 * because "6,633 against 6,633" is a fact a reader should not have to work out
 * by comparing digits.
 */
function bvHeadToHead(challenge: Challenge): HTMLElement {
  const teams = sidesByTeam(challenge);
  if (!teams.mine.total || !teams.theirs.total) return el("div");
  const { mine, theirs } = teams;
  const gap = Math.abs(mine.bv - theirs.bv) / Math.max(mine.bv, theirs.bv);

  const block = (
    force: Total,
    colour: string,
    align: string,
    label: string,
  ): HTMLElement => {
    const node = el("div", { className: `daily-bv-side ${align}` }, [
      el("p", {
        className: "daily-bv-n",
        textContent: number.format(force.bv),
      }),
      el("p", { className: "daily-bv-l", textContent: label }),
      // Only when the report measured them. A scenario indexed before these
      // were reported shows the battle value alone rather than a zero.
      ...(force.tons && force.cost
        ? [
            el("p", {
              className: "daily-bv-sub",
              textContent: `${number.format(force.tons)} t · ${cbill(force.cost)}`,
            }),
          ]
        : []),
    ]);
    node.style.setProperty("--side", colour);
    return node;
  };

  // One faction keeps its own name; several are counted, because "Talon Lance"
  // alone would be a lie about who the other 8,000 points belong to.
  const theirLabel =
    theirs.total === 1
      ? (theirs.name ?? "Opposition")
      : `${theirs.total} lances`;

  return el("div", { className: "daily-bv" }, [
    block(mine, sideCss(0), "daily-bv-left", mine.name ?? "You"),
    el("span", { className: "daily-bv-vs" }, [
      el("span", { className: "daily-bv-system", textContent: "BV 2.0" }),
      // Rounded, so a hair of difference does not read as a real edge.
      gap < 0.015 ? "even" : `${Math.round(gap * 100)}%`,
    ]),
    block(theirs, sideCss(1), "daily-bv-right", theirLabel),
  ]);
}

/** A side's totals, summed over every faction fighting on it. */
type Total = {
  bv: number;
  tons: number;
  cost: number;
  /** How many factions were summed. */
  total: number;
  /** Their name, when there is exactly one. */
  name?: string;
};

/**
 * The challenge's factions folded into the two sides that scoring uses.
 *
 * A side is a MegaMek team. The API scores the player's team against every
 * other team (`sides_from_result`), so the card adds up the same way rather
 * than showing four numerals a reader has to total themselves. A file that
 * never set `team_` falls back to first-faction-versus-the-rest, which is what
 * the two-faction dailies are.
 */
function sidesByTeam(challenge: Challenge): { mine: Total; theirs: Total } {
  const empty = (): Total => ({ bv: 0, tons: 0, cost: 0, total: 0 });
  const mine = empty();
  const theirs = empty();
  const myTeam = challenge.sides[0]?.team;
  for (const [index, side] of challenge.sides.entries()) {
    const isMine = myTeam === undefined ? index === 0 : side.team === myTeam;
    const into = isMine ? mine : theirs;
    into.bv += side.bv;
    into.tons += side.tons ?? 0;
    into.cost += side.cost ?? 0;
    into.total += 1;
    into.name = into.total === 1 ? side.name : undefined;
  }
  return { mine, theirs };
}

/**
 * A price in C-bills, short enough to sit under a battle value.
 *
 * A lance costs tens of millions and the exact figure is noise at this size,
 * so it is given to three significant figures with its scale as a letter -
 * 24.3M rather than 24,318,750.
 */
export function cbill(cost: number): string {
  if (cost >= 1e9) return `${(cost / 1e9).toFixed(2)}B C-bills`;
  if (cost >= 1e6) return `${(cost / 1e6).toFixed(1)}M C-bills`;
  if (cost >= 1e3) return `${Math.round(cost / 1e3)}K C-bills`;
  return `${number.format(cost)} C-bills`;
}

/**
 * What the fight changes about the game, above the way in.
 *
 * Grouped by kind rather than listed flat: a rule change and a bot setting are
 * different promises to a player, and the icon says which without a legend.
 * Empty for a challenge that changes nothing, which is most of them.
 */
function overrideList(challenge: Challenge): HTMLElement[] {
  const all = overridesFor(challenge.overrides);
  if (all.length === 0) return [];
  const groups: [OverrideKind, string][] = [
    ["rules", "Rules"],
    ["ai", "Opposition AI"],
  ];
  const rows: HTMLElement[] = [];
  for (const [kind, title] of groups) {
    const mine = all.filter((o) => o.kind === kind);
    if (mine.length === 0) continue;
    rows.push(
      el("div", { className: "daily-over-group" }, [
        el("p", { className: "daily-over-head", textContent: title }),
        ...mine.map((over) => row(over)),
      ]),
    );
  }
  return [el("div", { className: "daily-overrides" }, rows)];
}

type OverrideKind = Override["kind"];

/** One override: what it is, and what it was set to. */
function row(over: Override): HTMLElement {
  const node = el("p", { className: `daily-over daily-over-${over.kind}` }, [
    el("span", { className: "daily-over-mark", "aria-hidden": "true" }),
    el("span", { className: "daily-over-name", textContent: over.label }),
    ...(over.unofficial
      ? [el("span", { className: "daily-over-tag", textContent: "unofficial" })]
      : []),
    el("span", { className: "daily-over-value", textContent: over.value }),
  ]);
  // MegaMek's own explanation, on hover and to a screen reader. A rule that
  // changes how the game is played should be readable without leaving the
  // card, and this is the sentence the game itself would show.
  if (over.detail) node.title = over.detail;
  return node;
}

/**
 * The ground the fight is on, as a tall tile beside the fight's name.
 *
 * Beside the text rather than over the board: the board already has twelve
 * machines and their names on it, and an inset in its corner stood on whichever
 * force deployed there. Here it has its own room, and a 16x17 board is portrait
 * anyway - which is the shape this column has spare.
 *
 * The deployment zones are drawn over it in the colours their forces fight in,
 * so the tile says where the fight starts as well as what it is on.
 */
function terrainTile(challenge: Challenge): HTMLElement[] {
  const map = challenge.map;
  if (!map || !haveBoards(map.boards)) return [];
  const across = Math.max(1, map.cols);
  // The size the lattice actually wants, not the board's own proportions: a
  // hex column steps three quarters of a width across, so the two disagree by
  // about a sixth on a two-sheet map, and the slack is what the zone marks
  // were drifting across.
  const { width: wide, height: tall } = minimapSize(
    map.boards,
    across,
    300 * across,
  );

  // Turned a quarter only when it is actually wider than it is tall. Two
  // sheets side by side is the common daily and lands wide, which is the one
  // shape this column has no room for; a square or portrait map is already
  // the shape that fits and turning it would only make it wrong way up.
  const turn = wide > tall;
  const tile = el("canvas", { className: "daily-terrain" });
  tile.width = turn ? tall : wide;
  tile.height = turn ? wide : tall;
  tile.role = "img";
  tile.ariaLabel = "The ground the fight is on, and where each force deploys";

  const zones = challenge.sides
    .filter((side) => side.start)
    .map((side) => ({
      start: side.start!,
      colour: sideCssFor(challenge, challenge.sides.indexOf(side)),
    }));

  queueMicrotask(() => {
    if (!turn) {
      if (!drawMinimap(tile, map.boards, across, zones)) tile.hidden = true;
      return;
    }
    // Drawn the right way up on a scratch canvas, then turned onto the tile:
    // rotating the drawing itself would mean every hex, every zone box and
    // every measurement inside `drawMinimap` reasoning about which way is up.
    const flat = el("canvas");
    flat.width = wide;
    flat.height = tall;
    if (!drawMinimap(flat, map.boards, across, zones)) {
      tile.hidden = true;
      return;
    }
    const ctx = tile.getContext("2d");
    if (!ctx) return;
    ctx.translate(tile.width / 2, tile.height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.drawImage(flat, -wide / 2, -tall / 2);
  });
  const wrap = el("div", { className: "daily-terrain-tile" }, [tile]);
  // The frame takes its shape from the bitmap rather than the other way round.
  wrap.style.aspectRatio = `${tile.width} / ${tile.height}`;
  return [wrap];
}

/**
 * The card, for whichever fight today's is.
 *
 * Returns nothing when the schedule is empty, so an unscheduled site drops the
 * card rather than showing an empty one.
 */
export function dailyCard(): HTMLElement | null {
  const day = utcDay();
  const challenge = challengeFor(day);
  if (!challenge) return null;
  return challengeCard(challenge, day);
}

/**
 * One challenge, as a card: the board on the left and everything readable on
 * the right.
 *
 * The same panel the archive stacks, so a challenge looks like itself wherever
 * it is read, and the front page's copy is simply the one whose day is today.
 */
/**
 * The same picture the front page's card shows, without the way in.
 *
 * The lobby wants everything the card says about a fight — the board with both
 * forces on it, the ground, what each side is worth, what the fight changes —
 * and none of what the card *does*, because on that screen the way in is
 * Deploy. So the figure is separated from the card rather than copied: one
 * rendering of a fight, two places it is shown.
 *
 * `challenge` is null for a scenario out of the hand-written catalog, which
 * has a name and a description and nothing else — no board, no deployment
 * zones, no battle values, no boards to draw the ground from. Then this is the
 * name and the description, in the card's own type, and the parts with no data
 * behind them are left out rather than drawn empty.
 */
export function scenarioBrief(
  name: string,
  blurb: string,
  challenge: Challenge | null,
): HTMLElement {
  const text = el("div", { className: "daily-text-main" }, [
    el("h3", { className: "daily-brief-name", textContent: name }),
    el("p", { className: "hint daily-blurb", textContent: blurb }),
    ...(challenge ? [bvHeadToHead(challenge), ...overrideList(challenge)] : []),
  ]);
  return el("div", { className: "card daily daily-brief" }, [
    ...(challenge
      ? [el("div", { className: "daily-board" }, [boardFigure(challenge)])]
      : []),
    el("div", { className: "daily-text" }, [
      text,
      ...(challenge ? terrainTile(challenge) : []),
    ]),
  ]);
}

export function challengeCard(challenge: Challenge, day: string): HTMLElement {
  const play = playControl(challenge, day);
  // The one thing here that is not in the bundle: whether this reader has
  // already had their go. A card that waited for it would go blank whenever
  // the control plane did, so Play starts pressable and a spent attempt
  // disables it a moment later - and if the answer never comes, the API
  // refuses the press itself.
  //
  // The attempt *count* is deliberately not shown. A figure reading "0" for
  // most of a day says the fight is unplayed rather than new, which is the
  // opposite of an invitation.
  void dailyStatus(challenge.slug)
    .then((status) => {
      play.applyStatus(status);
    })
    .catch(() => {
      /* the press itself is still guarded, server-side */
    });

  return el("section", { className: "card daily" }, [
    el("div", { className: "daily-board" }, [boardFigure(challenge)]),
    el("div", { className: "daily-text" }, [
      // Everything readable in one column, and the ground beside it. Two
      // children rather than a dozen: the tile has to be as tall as the
      // whole column, and a grid item cannot stretch across rows that do
      // not exist yet.
      el("div", { className: "daily-text-main" }, [
        el("div", { className: "daily-head" }, [
          el("p", {
            className: "daily-eyebrow",
            textContent: "Daily challenge",
          }),
          // Only when it is *not* today. The front page carries one challenge
          // and this is it, so a "Today" badge beside it says nothing; in the
          // archive, where every row is some other day, the date is the point.
          ...(challenge.date === day
            ? []
            : [
                el("p", {
                  className: "daily-soon",
                  textContent: challenge.date,
                }),
              ]),
        ]),
        el("h2", { textContent: challenge.name }),
        el("p", {
          className: "hint daily-blurb",
          textContent: challenge.blurb,
        }),
        bvHeadToHead(challenge),
        ...overrideList(challenge),
        el("p", { className: "daily-actions" }, [
          // One control with two halves: the lit half starts the fight here,
          // the grey half takes the file away to play it in desktop MegaMek.
          // They are the same act - take on this scenario - so they read as
          // one object rather than a button and a stray link beside it.
          el("span", { className: "daily-launch" }, [
            play.button,
            el("a", {
              className: "daily-download plate plate-br",
              href: scenarioHref(challenge.slug),
              // download rather than navigate: a .mms is text, and a browser
              // that decides to render it instead of saving it has eaten the
              // link.
              download: `${challenge.slug}.mms`,
              textContent: "Download .mms",
            }),
          ]),
          play.note,
        ]),
      ]),
      ...terrainTile(challenge),
    ]),
  ]);
}
